import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// Cinematic zero-click entry: hold the TAB seal + title on a solid background for ~2s,
// then cross-fade (3s) to reveal the levitating-globe gallery. No Enter button.
export function LoadingScreen({ ready }) {
  const [phase, setPhase] = useState("hold"); // hold -> fade -> done
  const t0 = useRef(performance.now());

  useEffect(() => {
    if (phase !== "hold") return;
    let raf;
    const tick = () => {
      const elapsed = performance.now() - t0.current;
      if (elapsed >= 2000 && ready) {
        setPhase("fade");
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, ready]);

  useEffect(() => {
    if (phase !== "fade") return;
    const tm = setTimeout(() => setPhase("done"), 3000);
    return () => clearTimeout(tm);
  }, [phase]);

  if (phase === "done") return null;
  const fading = phase === "fade";

  return (
    <motion.div
      data-testid="brand-intro"
      initial={{ opacity: 1 }}
      animate={{ opacity: fading ? 0 : 1 }}
      transition={{ duration: fading ? 3 : 0.3, ease: "easeInOut" }}
      style={{ pointerEvents: fading ? "none" : "auto" }}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#030712]"
    >
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse at 50% 45%, rgba(212,175,55,0.10) 0%, rgba(3,7,18,0.96) 62%, #030712 100%)" }}
      />

      <div className="relative flex flex-col items-center">
        <motion.img
          data-testid="brand-intro-seal"
          src="/tab-seal.png"
          alt="The Academic Boardroom Seal"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{
            opacity: 1,
            scale: [1, 1.035, 1],
            filter: [
              "drop-shadow(0 0 18px rgba(212,175,55,0.30))",
              "drop-shadow(0 0 58px rgba(212,175,55,0.78))",
              "drop-shadow(0 0 18px rgba(212,175,55,0.30))",
            ],
          }}
          transition={{
            opacity: { duration: 1.1, ease: "easeOut" },
            scale: { duration: 4.2, repeat: Infinity, ease: "easeInOut" },
            filter: { duration: 4.2, repeat: Infinity, ease: "easeInOut" },
          }}
          className="w-[clamp(170px,26vw,300px)] h-[clamp(170px,26vw,300px)] rounded-full object-cover ring-1 ring-[rgba(212,175,55,0.45)]"
        />

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1 }}
          style={{ fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif' }}
          className="mt-9 text-center text-3xl sm:text-5xl font-semibold tracking-tight text-[#f4ede0]"
        >
          Anatomy of an Earthquake
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 1 }}
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
          className="mt-4 text-center text-[11px] sm:text-xs uppercase tracking-[0.42em] text-[#c9a94a]"
        >
          The Academic Boardroom
        </motion.p>
      </div>
    </motion.div>
  );
}
